import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send, MessageSquareText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const TYPES = ["Feedback", "Bug", "Suggestie"];

export function FeedbackDialog({ open, onOpenChange }) {
  const [message, setMessage] = useState("");
  const [type, setType] = useState("Feedback");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setMessage("");
    setType("Feedback");
  };

  const close = () => {
    if (submitting) return;
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    const msg = message.trim();
    if (!msg) {
      toast.error("Please enter your feedback before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        message: msg,
        type,
        page_url: typeof window !== "undefined" ? window.location.href : null,
      });
      toast.success("Feedback verzonden!");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent
        data-testid="feedback-dialog"
        className="bg-tw-surface border-tw text-white rounded-none max-w-lg sm:max-w-xl p-0 gap-0"
      >
        {/* Yellow tactical stripe */}
        <div className="h-1 w-full bg-tw-yellow" />

        <div className="p-6 sm:p-8">
          <DialogHeader className="space-y-3 mb-6">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-tw-muted">
              <MessageSquareText size={14} className="text-tw-yellow" />
              SMART FEEDBACK
            </div>
            <DialogTitle className="font-display text-3xl sm:text-4xl font-black text-white uppercase tracking-tight leading-none">
              Share your thoughts
            </DialogTitle>
            <DialogDescription className="text-sm text-tw-muted">
              Report a bug, drop feedback, or suggest an improvement. It goes straight to the TW1 vault and the Feedback Hub.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted mb-2">TYPE</div>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger
                  data-testid="feedback-type-select"
                  className="bg-tw-black border-tw text-white h-11 rounded-none focus:ring-0 focus:ring-offset-0 focus:border-tw-yellow"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
                  {TYPES.map((t) => (
                    <SelectItem
                      key={t}
                      value={t}
                      data-testid={`feedback-type-option-${t}`}
                      className="focus:bg-tw-yellow focus:text-black rounded-none uppercase tracking-wider text-sm"
                    >
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted mb-2">MESSAGE</div>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe what happened, what you'd like changed, or your idea…"
                data-testid="feedback-message-input"
                rows={6}
                className="bg-tw-black border-tw text-white rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow resize-none"
              />
              <div className="mt-2 flex justify-between font-mono text-[10px] text-tw-muted">
                <span>{message.trim().split(/\s+/).filter(Boolean).length} WORDS</span>
                <span>{message.length}/5000</span>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-8 gap-3 sm:gap-3 flex-col-reverse sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={close}
              disabled={submitting}
              data-testid="feedback-cancel-btn"
              className="h-11 rounded-none bg-transparent border-tw text-white hover:bg-tw-surface-hover hover:text-white uppercase tracking-[0.2em] text-xs"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !message.trim()}
              data-testid="feedback-submit-btn"
              className="h-11 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-xs font-bold disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>
                  <Send size={14} className="mr-2" />
                  Submit
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
